
import Vue from "vue";
import {Component, Prop} from "vue-property-decorator";
import {HcPageModel} from "@dev/hc-components";
import ICurrency from "@/module/main/dto/ICurrency";
import {EnumUtil} from "@/app";
import ILoan from "@/module/main/model/applicationForm/ILoan";
import FinancedSubjectType from "@/module/main/dto/FinancedSubjectTypeEnum";
import Culture from "@/module/main/page/CultureEnum";
import ClientType from "@/module/main/dto/ClientTypeEnum";
import OrderType from "@/module/main/dto/OrderTypeEnum";
import FinancingType from "@/module/main/dto/FinancingTypeEnum";
import OperatingLeaseProductsPerson from "@/module/main/model/applicationForm/OperatingLeaseProductsPersonEnum";
import OperatingLeaseProductsEntrepreneur from "@/module/main/model/applicationForm/OperatingLeaseProductsEntrepreneur";
import OperatingLeaseProductsLegalEntity from "@/module/main/model/applicationForm/OperatingLeaseProductsLegalEntity";
import CashFinancingType from "@/module/main/dto/CashFinancingTypeEnum";
import YesNo from "@/app/service/YesNoEnum";

@Component
export default class Loan extends HcPageModel {
    @Prop({ type: Object }) public value!: ILoan;
    @Prop({ type: Object }) public currency!: ICurrency;
    @Prop({ type: String }) public culture!: Culture;

    protected pageName: string = "loan";

    private get isOrder()
    {
        return this.value.financedSubjectType === FinancedSubjectType.ORDER;
    }

    private get isWallet()
    {
        return this.value.financedSubjectType === FinancedSubjectType.WALLET;
    }

    private get isOperatingLease()
    {
        return this.value.orderType === OrderType.OPERATING_LEASE;
    }

    private get isStandardOrder()
    {
        return this.value.financedSubjectType === FinancedSubjectType.ORDER && this.value.orderType == OrderType.STANDARD;
    }

    private get isBrokeredOrder()
    {
        return this.value.financedSubjectType === FinancedSubjectType.ORDER && this.value.orderType == OrderType.BROKERED;
    }

    private get isCash() {
        return this.value.financedSubjectType === FinancedSubjectType.CASH;
    }

    private get isLiability() {
        return this.value.financedSubjectType === FinancedSubjectType.LIABILITY;
    }

    private get isCarLoan() {
        return this.isOrder && this.value.orderType === OrderType.CAR_LOAN;
    }

    private get isPerson() {
      return this.value.clientType == ClientType.PERSON;
    }

    private get totalPriceKey() {
        return (this.isOrder || this.isWallet) ? 'loan-order-total-price' : 'loan-total-price';
    }

    private get financedSubjectData(): object[] {
        // TODO: WALLET temporarily hidden - remove filter once WALLET is ready
        return EnumUtil.createSelectDataFromEnum(FinancedSubjectType, "FinancedSubjectType")
            .filter(item => item.value !== FinancedSubjectType.WALLET);
    }

    private get clientTypeData(): object[]
    {
        let data = EnumUtil.createSelectDataFromEnum(ClientType, "ClientType");

        if (this.isCash || this.isStandardOrder)
        {
            //remove LEGAL_ENTITY
            const index = data.findIndex(item => item.value === ClientType.LEGAL_ENTITY);
            if (index !== -1) {
                data.splice(index, 1);
            }

            //change ENTREPRENEUR text
            data = data.map(item =>
                item.value === ClientType.ENTREPRENEUR ?
                    {...item, label: Vue.prototype.$localize("ClientType-ENTREPRENEUR-CASH")}
                    : item
            );
        }

        return data;
    }

    private get orderTypeData(): object[] {
        return EnumUtil.createSelectDataFromEnum(OrderType, "OrderType");
    }

    private get cashFinancingTypeData(): object[] {
        return EnumUtil.createSelectDataFromEnum(CashFinancingType, "CashFinancingType");
    }

    private get walletWithOrderData(): object[] {
        return EnumUtil.createSelectDataFromEnum(YesNo, "WalletWithOrder");
    }

    private get financingTypeData(): object[] {
        let financingTypes = EnumUtil.createSelectDataFromEnum(FinancingType, "FinancingType");

        const index = financingTypes.findIndex(item => item.value === FinancingType.OPERATING_LEASE);
        if (index !== -1) {
          financingTypes.splice(index, 1);
        }

        return financingTypes;
    }

    private get operatingLeaseProductsData(): object[]
    {
        switch (this.value.clientType)
        {
            case ClientType.PERSON:
                let values = EnumUtil.createSelectDataFromEnum(OperatingLeaseProductsPerson, "OperatingLeaseProductsPerson");
                //TODO: remove product codes unavailable in specific cultures - remove when it will be united for all cultures
                const unavailableProductCodes = (this.culture === Culture.CZ) ? ['OL24'] : ['OL01'];
                return values.filter(item => !unavailableProductCodes.includes(item.value));
            case ClientType.ENTREPRENEUR:
                return EnumUtil.createSelectDataFromEnum(OperatingLeaseProductsEntrepreneur, "OperatingLeaseProductsEntrepreneur");
            case ClientType.LEGAL_ENTITY:
                return EnumUtil.createSelectDataFromEnum(OperatingLeaseProductsLegalEntity, "OperatingLeaseProductsLegalEntity");
        }
    }

    private get getTitle(): string {
        return this.value.financedSubjectType === FinancedSubjectType.ORDER ? "loan-title.ORDER" : "loan-title";
    }

    private financedSubjectTypeChanged(financedSubjectType: FinancedSubjectType): void {
        this.$store.dispatch("main/setFinancedSubjectType", financedSubjectType);
        this.$emit("financedSubjectTypeChanged", financedSubjectType);
    }

    private clientTypeChanged(clientType: ClientType): void {
        if (clientType === ClientType.PERSON) {
            this.value.cashFinancingType = CashFinancingType.LOAN_WALLET;
            this.$store.dispatch("main/setCashFinancingType", CashFinancingType.LOAN_WALLET);
      }

        this.$store.dispatch("main/setClientType", clientType);
        this.$emit("financedSubjectTypeChanged", this.value.financedSubjectType);
    }

    private orderTypeChanged(orderType: OrderType): void {
        this.$store.dispatch("main/setOrderType", orderType);
        this.$emit("financedSubjectTypeChanged", this.value.financedSubjectType);
    }

    private financingTypeChanged(financingType: FinancingType): void {
        this.$store.dispatch("main/setFinancingType", financingType);
        this.$emit("financedSubjectTypeChanged", this.value.financedSubjectType);
    }

    private cashFinancingTypeChanged(cashFinancingType: CashFinancingType): void {
        this.$store.dispatch("main/setCashFinancingType", cashFinancingType);
        this.$emit("financedSubjectTypeChanged", this.value.financedSubjectType);
    }

    private walletWithOrderChanged(walletWithOrder: YesNo): void {
        this.$store.dispatch("main/setWalletWithOrder", walletWithOrder);
        this.$emit("financedSubjectTypeChanged", this.value.financedSubjectType);
    }

    private setFormData(): void {
        let financedSubjectType: string = this.$store.getters["main/getFinancedSubjectType"];
        !!financedSubjectType && (this.value.financedSubjectType = FinancedSubjectType[financedSubjectType as FinancedSubjectType]);
        this.financedSubjectTypeChanged(this.value.financedSubjectType);
    }

    public created(): void {
        this.setFormData();
    }
}
